/**
 * Local fetch-based service for the XEPPT Order Block endpoint.
 * Does NOT use the xeppt-sdk — this endpoint extends the Orders API and is
 * not yet published in @xeppt/xeppt-sdk's OrdersService, same situation as
 * refund_service.ts and merchant_config_service.ts.
 *
 * AUTH: Bearer access token from localStorage 'client-metadata', same
 * pattern as refund_service.ts and merchant_config_service.ts.
 *
 * API CONTRACT (backend live):
 *   POST /orders/{orderId}/block   (no request body)
 *   Blocks the order's consumer for this business by reusing the existing
 *   contact-block system — the consumer then appears via
 *   apiContactService.getBlockedContacts() and can be unblocked via
 *   apiContactService.unblockContact() / PATCH /contact/unblock.
 *   → 2xx with no meaningful body on success.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? '';

function authHeaders(): Record<string, string> {
    try {
        const raw = localStorage.getItem('client-metadata');
        if (!raw) return {};
        const meta = JSON.parse(raw) as { accessToken?: string };
        return meta?.accessToken ? { Authorization: `Bearer ${meta.accessToken}` } : {};
    } catch {
        return {};
    }
}

// ─── Error class ─────────────────────────────────────────────────────────────
// Named `status`/`code` (not `errorCode`/`statusCode`) to match the other
// local (non-SDK) service error classes in this directory — callers handle
// it with an `instanceof` check + `err.message` fallback, same as
// RefundApiError in view_order's own RefundOrderSubview.

export class OrderBlockApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
        options?: ErrorOptions
    ) {
        super(message, options);
        this.name = 'OrderBlockApiError';
    }
}

async function throwOnError(response: Response): Promise<never> {
    let code: string | undefined;
    let message: string | undefined;
    try {
        const errBody = (await response.json()) as { code?: string; message?: string };
        code = typeof errBody?.code === 'string' ? errBody.code : undefined;
        message = typeof errBody?.message === 'string' ? errBody.message : undefined;
    } catch {
        // ignore JSON parse failures on error bodies
    }
    throw new OrderBlockApiError(
        message ?? `HTTP ${response.status}`,
        response.status,
        code
    );
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const apiOrderBlockService = {
    /**
     * POST /orders/{orderId}/block — blocks the order's consumer for this
     * business. No request body.
     */
    async blockCustomer(orderId: string): Promise<void> {
        const response = await fetch(`${BACKEND_URL}/orders/${orderId}/block`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() }
        });
        if (!response.ok) {
            await throwOnError(response);
        }
    }
};
